<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue';
    import ModalBase from '@/shared/components/ModalBase.vue';
    import DateTimePicker from '@/shared/components/DateTimePicker.vue';
    import { getAttributeDefinitions } from '@/shared/helpers/api';
    import { AttributeDefinition } from '@/shared/helpers/api/types/entities.ts';
    import { ReferenceFilterNode } from '@/shared/codeedit/dynamicFilterTypes.ts';
    import { builtInFields, isFilterableType } from '@/shared/codeedit/dynamicFilterRules.ts';
    import {
        availableOperators,
        availableParams,
        availableValueModes,
        buildPredicate,
        caseSensitivityLock,
        ClauseContext,
        ClauseRow,
        filterHasErrors,
        literalInputType,
        newClause,
        onAttributeChanged,
        onOpChanged,
        parseExistingPredicate,
        refFieldType,
        valueMessage,
        withLibraryPrefix
    } from '@/shared/codeedit/dynamicFilterClauses.ts';
    import { http } from '@/shared/httpWrapper';

    const props = defineProps<{
        referenceLibraryName: string;
        currentLibraryName: string;
        currentLibraryAttrDefs: AttributeDefinition[];
        existingPredicate: ReferenceFilterNode | null;
        // Whether each library is case-sensitive, by lower-case name
        libraryCaseSensitivity?: ReadonlyMap<string, boolean>;
    }>();

    const emit = defineEmits<{
        confirm: [predicateJson: ReferenceFilterNode];
        clear: [];
        cancel: [];
    }>();

    type LoadStatus = 'loading' | 'ready' | 'failed';

    const logicalOp = ref<'And' | 'Or'>('And');
    const clauses = ref<ClauseRow[]>([]);
    const refLibAttrDefs = ref<AttributeDefinition[]>([]);
    const status = ref<LoadStatus>('ready');

    // A non-filterable column is left out, so a filter can never come to depend on one
    const columnOptions = computed(() =>
        withLibraryPrefix(props.referenceLibraryName, [
            ...builtInFields,
            ...refLibAttrDefs.value.filter(a => isFilterableType(a.attributeType)).map(a => a.name)
        ]));

    const clauseContext = computed<ClauseContext>(() => ({
        currentLibraryName: props.currentLibraryName,
        currentLibraryAttrDefs: props.currentLibraryAttrDefs,
        refLibAttrDefs: refLibAttrDefs.value,
        libraryCaseSensitivity: props.libraryCaseSensitivity ?? new Map()
    }));

    const valueMessages = computed<Record<string, string>>(() =>
        Object.fromEntries(clauses.value.map(c => [c.id, valueMessage(c, clauseContext.value)])));

    const caseSensitivityLocks = computed(() =>
        Object.fromEntries(clauses.value.map(c => [c.id, caseSensitivityLock(c, clauseContext.value)])));

    function addClause() {
        clauses.value.push(newClause());
    }

    function removeClause(idx: number) {
        clauses.value.splice(idx, 1);
    }

    const isEmptyFilter = computed<boolean>(() => clauses.value.length === 0);

    const hasErrors = computed<boolean>(() =>
        filterHasErrors(clauses.value, clauseContext.value));

    onMounted(async () => {
        // Parsed before the request so that a failed load cannot leave the modal showing an empty
        // rule list, which Save would then interpret as "clear the filter"
        const parsed = parseExistingPredicate(props.existingPredicate);
        if (parsed) {
            logicalOp.value = parsed.logicalOp;
            clauses.value = parsed.clauses;
        }

        if (!props.referenceLibraryName) return;

        status.value = 'loading';
        try {
            refLibAttrDefs.value = await getAttributeDefinitions(http, props.referenceLibraryName);
            status.value = 'ready';
        } catch {
            status.value = 'failed';
        }
    });

    function onClearFilter() {
        clauses.value = [];
    }

    function onConfirm() {
        // Without the reference library's attribute types every clause would be saved with an
        // 'Unknown' group, so nothing may be saved until they load
        if (status.value === 'failed') return;

        if (isEmptyFilter.value) {
            emit('clear');
        } else if (!hasErrors.value) {
            emit('confirm', buildPredicate(clauses.value, logicalOp.value, clauseContext.value));
        }
    }
</script>

<template>
    <modal-base
        max-width="1200px"
        width="65rem"
        @on-cancel="emit('cancel')">
        <template #modal-header>
            Dynamic CodeRef filter rules
        </template>
        <template #modal-body>
            <p class="mb-3 has-text-grey">
                Each rule compares a reference field against a current form value or a fixed value.
            </p>
            <div
                v-if="status === 'loading'"
                class="has-text-centered py-4">
                <b-icon
                    icon="loading"
                    custom-class="mdi-spin"
                    size="is-medium" />
                <p class="mt-2 has-text-grey">
                    Loading columns…
                </p>
            </div>
            <div
                v-else-if="status === 'failed'"
                class="has-text-centered py-4">
                <b-icon
                    icon="alert-circle-outline"
                    type="is-danger"
                    size="is-medium" />
                <p class="mt-2 has-text-danger">
                    Could not load the columns of <strong>{{ props.referenceLibraryName }}</strong>.
                </p>
                <p class="has-text-grey">
                    The existing filter has been left untouched. Close the dialog and try again.
                </p>
            </div>
            <template v-else>
                <div
                    v-if="clauses.length > 1"
                    class="is-flex is-align-items-center gap-2 mb-5">
                    <label class="label">Match:</label>
                    <b-select v-model="logicalOp">
                        <option value="And">
                            All rules must match (AND)
                        </option>
                        <option value="Or">
                            One rule must match (OR)
                        </option>
                    </b-select>
                </div>

                <div class="clauses-container">
                    <div class="clause-header">
                        <div>
                            Reference field
                        </div>
                        <div>
                            Condition
                        </div>
                        <div>
                            Compare to
                        </div>
                        <div>
                            Value
                        </div>
                        <div class="case-sensitive-cell">
                            Case-sensitive
                        </div>
                        <div />
                    </div>
                    <div class="clause-scroll">
                        <div
                            v-for="(clause, idx) in clauses"
                            :key="clause.id"
                            class="clause-row">
                            <div>
                                <b-field>
                                    <b-select
                                        v-model="clause.attribute"
                                        expanded
                                        @update:model-value="() => onAttributeChanged(clause, clauseContext)">
                                        <option
                                            v-for="col in columnOptions"
                                            :key="col.value"
                                            :value="col.value">
                                            {{ col.label }}
                                        </option>
                                    </b-select>
                                </b-field>
                            </div>

                            <div>
                                <b-field>
                                    <b-select
                                        v-model="clause.op"
                                        expanded
                                        @update:model-value="() => onOpChanged(clause)">
                                        <option
                                            v-for="op in availableOperators(clause, clauseContext)"
                                            :key="op.value"
                                            :value="op.value">
                                            {{ op.label }}
                                        </option>
                                    </b-select>
                                </b-field>
                            </div>

                            <div>
                                <b-field>
                                    <b-select
                                        v-model="clause.valueMode"
                                        expanded>
                                        <option
                                            v-for="mode in availableValueModes(clause.op)"
                                            :key="mode.value"
                                            :value="mode.value">
                                            {{ mode.label }}
                                        </option>
                                    </b-select>
                                </b-field>
                            </div>

                            <div
                                class="value-cell"
                                @focusout="clause.valueTouched = true">
                                <b-field
                                    :message="valueMessages[clause.id]"
                                    :type="valueMessages[clause.id] ? 'is-danger' : ''">
                                    <b-select
                                        v-show="clause.valueMode === 'param'"
                                        v-model="clause.param"
                                        expanded>
                                        <option
                                            v-for="p in availableParams(clause, clauseContext)"
                                            :key="p.value"
                                            :value="p.value">
                                            {{ p.label }}
                                        </option>
                                    </b-select>
                                    <b-select
                                        v-if="clause.valueMode === 'literal' && literalInputType(clause, clauseContext) === 'bool-select'"
                                        v-model="clause.literal"
                                        expanded>
                                        <option :value="true">
                                            true
                                        </option>
                                        <option :value="false">
                                            false
                                        </option>
                                    </b-select>
                                    <b-input
                                        v-else-if="clause.valueMode === 'literal' && literalInputType(clause, clauseContext) === 'number'"
                                        v-model="clause.literal"
                                        type="number"
                                        step="any"
                                        expanded
                                        placeholder="number" />
                                    <date-time-picker
                                        v-else-if="clause.valueMode === 'literal' && literalInputType(clause, clauseContext) === 'temporal'"
                                        :input="String(clause.literal)"
                                        :date-only="refFieldType(clause.attribute, clauseContext) === 'Date'"
                                        :stack-date-and-time-input="true"
                                        :append-to-body="true"
                                        @dt-picker="(v: string) => clause.literal = v" />
                                    <b-input
                                        v-else-if="clause.valueMode === 'literal'"
                                        v-model="clause.literal"
                                        expanded
                                        :placeholder="clause.op === 'In' ? 'val1, val2, val3' : 'text'" />
                                </b-field>
                            </div>

                            <div class="case-sensitive-cell">
                                <b-tooltip
                                    :label="caseSensitivityLocks[clause.id]?.reason"
                                    :active="caseSensitivityLocks[clause.id] !== null"
                                    position="is-left"
                                    multilined>
                                    <b-checkbox
                                        :model-value="caseSensitivityLocks[clause.id]?.matchesCase ?? clause.caseSensitive"
                                        :disabled="caseSensitivityLocks[clause.id] !== null"
                                        :aria-label="`Case-sensitive (rule ${idx + 1})`"
                                        @update:model-value="(checked: boolean) => clause.caseSensitive = checked" />
                                </b-tooltip>
                            </div>

                            <div>
                                <b-button
                                    icon-left="minus"
                                    @click="removeClause(idx)" />
                            </div>
                        </div>
                    </div>

                    <div class="clause-add-row">
                        <b-button
                            icon-left="plus"
                            @click="addClause">
                            Add rule
                        </b-button>
                    </div>
                </div>
            </template>
        </template>
        <template #modal-footer>
            <div class="buttons">
                <b-button
                    label="Cancel"
                    @click="emit('cancel')" />
                <b-button
                    label="Clear filter"
                    type="is-danger is-light"
                    :disabled="status === 'failed'"
                    @click="onClearFilter" />
                <b-button
                    label="Confirm filter"
                    type="is-primary"
                    :disabled="hasErrors || status === 'failed'"
                    @click="onConfirm" />
            </div>
        </template>
    </modal-base>
</template>

<style scoped>
.clauses-container {
    border: 1px solid var(--bulma-border);
    border-radius: var(--bulma-radius);
}

.clause-scroll {
    max-height: 35rem;
    overflow-y: auto;
    scrollbar-gutter: stable;
}

.case-sensitive-cell {
    text-align: center;
    line-height: 1.2;
}

/* Buefy spaces a checkbox from its label, which is empty here, so both are reset to keep the box centred */
.case-sensitive-cell :deep(.b-checkbox.checkbox) {
    margin-right: 0;
}

.case-sensitive-cell :deep(.control-label:empty) {
    display: none;
}

.clause-header,
.clause-row {
    display: grid;
    grid-template-columns: 1fr 8.25rem 11.75rem 1fr 4.5rem 2.5rem;
    gap: 0.75rem;
    padding: 0.75rem 0.75rem 1.5rem;
    align-items: center;
}

.clause-row :deep(.field) {
    position: relative;
}

.clause-row :deep(.help) {
    position: absolute;
    top: 100%;
}

/* Reserves the same scrollbar space as .clause-scroll so the columns line up, with the background painted under it */
.clause-header {
    background: var(--bulma-background);
    border-bottom: 2px solid var(--bulma-border);
    font-weight: var(--bulma-weight-bold);
    overflow-y: hidden;
    scrollbar-gutter: stable;
    padding-bottom: 0.75rem;
}

.clause-add-row {
    padding: 0.75rem;
}

</style>
